/**
 * Shared utilities for Global-e Shipping Components
 * Used by both Cart and PDP components
 */

import { globaleApiClient } from '@theme/globale-api-client';

// Shared constants
export const CURRENCY_SYMBOL_DEFAULT = '$';
export const SHIPPING_DEFAULT = 'Shipping';
export const CLASS_HIDDEN = 'hidden';
export const SELECTOR_SHIPPING_CARD = '.globale-shipping-card';
export const MAX_RATES_DISPLAY = 2;
export const PRICE_DIVISOR = 100;
export const PRICE_THRESHOLD = 1000;
export const PRICE_DECIMALS = 2;
export const MIN_PRICE = 0.01;

// Error constants
const ERROR_FAILED_FETCH = 'Failed to fetch';
const ERROR_PROXY = 'Proxy';

/**
 * @typedef {Object} ShippingRate
 * @property {string} courierName
 * @property {number} cost
 * @property {string} currency
 * @property {string|null} estimatedDays
 * @property {boolean} available
 * @property {number} [minOrderSubtotal]
 * @property {number} [maxOrderSubtotal]
 */

/**
 * @typedef {Object} ShippingData
 * @property {ShippingRate[]} shippingRates
 * @property {string} currencyCode
 * @property {string} [destinationCountry]
 */

/**
 * Transform API response format to internal format
 * @param {any} apiResponse
 * @param {number|undefined} [orderTotal] - Current order total for filtering rates by minOrderSubtotal/maxOrderSubtotal
 * @returns {ShippingData}
 */
export function transformApiResponse(apiResponse, orderTotal) {
  if (!apiResponse || !Array.isArray(apiResponse) || apiResponse.length === 0) {
    return { shippingRates: [], currencyCode: '' };
  }

  const firstResult = apiResponse[0];
  const shippingRates = firstResult.shippingRates || [];

  const currencyCode = shippingRates.length > 0
    ? shippingRates[0].shippingCostCurrency || ''
    : '';

  const transformedRates = shippingRates.map((/** @type {any} */ rate) => ({
    courierName: rate.shippingPriority || SHIPPING_DEFAULT,
    cost: rate.shippingCost || 0,
    currency: rate.shippingCostCurrency || currencyCode,
    estimatedDays: rate.estimatedDeliveryDays || null,
    available: true,
    minOrderSubtotal: rate.minOrderSubtotal,
    maxOrderSubtotal: rate.maxOrderSubtotal
  }));

  // Filter rates based on order total (if provided)
  const filteredRates = orderTotal ? filterRatesByOrderTotal(transformedRates, orderTotal) : transformedRates;

  return {
    shippingRates: filteredRates,
    currencyCode: currencyCode,
    destinationCountry: firstResult.destinationCountry
  };
}

/**
 * Filter shipping rates based on order total and minOrderSubtotal/maxOrderSubtotal constraints
 * @param {ShippingRate[]} rates
 * @param {number} orderTotal
 * @returns {ShippingRate[]}
 */
function filterRatesByOrderTotal(rates, orderTotal) {
  return rates.filter(rate => {
    // If rate has minOrderSubtotal, order total must be >= minOrderSubtotal
    if (rate.minOrderSubtotal && orderTotal < rate.minOrderSubtotal) {
      return false;
    }

    // If rate has maxOrderSubtotal, order total must be <= maxOrderSubtotal
    if (rate.maxOrderSubtotal && orderTotal > rate.maxOrderSubtotal) {
      return false;
    }

    return true;
  });
}

/**
 * Get customer country from Shopify market data
 * @param {HTMLElement|null} [element] - Component element to read data attributes from
 * @returns {Promise<string|null>}
 */
export async function getCustomerCountry(element = null) {
  // Get country from data-country attribute (injected from Liquid)
  if (element?.dataset.country) {
    return element.dataset.country;
  }

  // Fallback: window.GlobaleConfig (if available)
  const win = /** @type {Window & {GlobaleConfig?: {market?: {country?: string}}; Shopify?: {country?: string}}} */ (window);
  if (win.GlobaleConfig?.market?.country) {
    return win.GlobaleConfig.market.country;
  }

  // Last fallback: Shopify.country
  return win.Shopify?.country || null;
}

/**
 * Get customer currency from Shopify market data
 * @param {string|null} fallbackCurrency
 * @param {HTMLElement|null} [element] - Component element to read data attributes from
 * @returns {Promise<string|null>}
 */
export async function getCustomerCurrency(fallbackCurrency = null, element = null) {
  // Get currency from data-currency attribute (injected from Liquid)
  if (element?.dataset.currency) {
    return element.dataset.currency;
  }

  // Fallback: window.GlobaleConfig (if available)
  const win = /** @type {Window & {GlobaleConfig?: {market?: {currency?: string}}; Shopify?: {currency?: {active?: string}}}} */ (window);
  if (win.GlobaleConfig?.market?.currency) {
    return win.GlobaleConfig.market.currency;
  }

  // Last fallback: Shopify.currency.active
  return win.Shopify?.currency?.active || fallbackCurrency || null;
}

/**
 * Fetch shipping details from Global-e API
 * @param {Object} params
 * @param {HTMLElement} params.element - Component element
 * @param {Function} params.getData - Function to get cart/product data
 * @returns {Promise<ShippingData>}
 */
export async function fetchShippingDetails({ element, getData }) {
  const data = getData();
  if (!data) {
    throw new Error('Data not available');
  }

  const country = await getCustomerCountry(element);
  if (!country) {
    throw new Error('Unable to detect country');
  }

  const currency = await getCustomerCurrency(data.currency, element);
  const amount = data.totalPrice || data.price;
  const productCodes = data.productCodes || [data.productCode].filter(Boolean);

  if (!amount || amount <= 0) {
    throw new Error('Amount is not available');
  }

  if (!productCodes || productCodes.length === 0) {
    throw new Error('Product codes are not available');
  }

  try {
    const apiResponse = await globaleApiClient.getShippingDetails({
      country,
      amount,
      currency: currency || data.currency,
      productCodes,
      element
    });

    // Pass order total to filter rates by minOrderSubtotal/maxOrderSubtotal
    return transformApiResponse(apiResponse, amount);
  } catch (error) {
    throw error;
  }
}

/**
 * Get currency symbol from data-currency-symbol attribute
 * @param {HTMLElement|null} [element] - Element to read data-currency-symbol attribute from
 * @returns {string}
 */
export function getCurrencySymbol(element = null) {
  return element?.dataset?.currencySymbol || CURRENCY_SYMBOL_DEFAULT;
}

/**
 * Format price
 * @param {number} cost
 * @param {string} currency
 * @param {string} symbol
 * @returns {string}
 */
export function formatPrice(cost, currency, symbol) {
  if (cost > PRICE_THRESHOLD) {
    return `${symbol}${Number(cost).toLocaleString('en-US')}`;
  }
  return `${symbol}${cost.toFixed(PRICE_DECIMALS)}`;
}

/**
 * Escape HTML
 * @param {string} text
 * @returns {string}
 */
export function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Get translation with optional placeholder replacement
 * @param {string} key - Translation key
 * @param {Record<string, string>} params - Optional parameters for placeholder replacement
 * @returns {string} - Translated string
 */
export function getTranslation(key, params = {}) {
  const keys = key.split('.');
  let value = Theme?.translations;
  
  if (!value) return key;
  
  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = /** @type {any} */ (value)[k];
    } else {
      return key;
    }
    if (value === undefined) return key;
  }
  
  if (typeof value !== 'string') return key;
  
  // Replace placeholders like {{ error }}
  const translationString = /** @type {string} */ (value);
  return translationString.replace(/\{\{\s*(\w+)\s*\}\}/g, (/** @type {string} */ match, /** @type {string} */ paramKey) => {
    return params[paramKey] !== undefined ? String(params[paramKey]) : match;
  });
}

/**
 * Debounce utility function
 * @param {Function} func
 * @param {number} wait
 * @returns {Function}
 */
export function debounce(func, wait) {
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let timeout;
  // @ts-ignore - args type is handled by Function type
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Get single element from refs (handle Element | Element[] case)
 * @param {HTMLElement} element - Component element
 * @param {string} key - Ref key
 * @returns {HTMLElement|null}
 */
export function getRefElement(element, key) {
  // @ts-ignore - refs can be Element | Element[], handled by runtime check
  const ref = element.refs?.[key];
  if (!ref) return null;
  if (Array.isArray(ref)) return ref[0] instanceof HTMLElement ? ref[0] : null;
  return ref instanceof HTMLElement ? ref : null;
}

/**
 * Show loading state
 * @param {HTMLElement} element - Component element
 */
export function showLoadingState(element) {
  const loading = getRefElement(element, 'loading');
  const error = getRefElement(element, 'error');
  const empty = getRefElement(element, 'empty');
  const methods = getRefElement(element, 'methods');

  loading?.classList.remove(CLASS_HIDDEN);
  error?.classList.add(CLASS_HIDDEN);
  empty?.classList.add(CLASS_HIDDEN);
  methods?.classList.add(CLASS_HIDDEN);
}

/**
 * Show error state
 * @param {HTMLElement} element - Component element
 * @param {string|Error} message - Error message
 */
export function showErrorState(element, message) {
  const loading = getRefElement(element, 'loading');
  const error = getRefElement(element, 'error');
  const empty = getRefElement(element, 'empty');
  const methods = getRefElement(element, 'methods');

  loading?.classList.add(CLASS_HIDDEN);
  error?.classList.remove(CLASS_HIDDEN);
  empty?.classList.add(CLASS_HIDDEN);
  methods?.classList.add(CLASS_HIDDEN);

  if (error) {
    error.textContent = message instanceof Error ? message.message : message;
  }
}

/**
 * Show empty state
 * @param {HTMLElement} element - Component element
 */
export function showEmptyState(element) {
  const loading = getRefElement(element, 'loading');
  const error = getRefElement(element, 'error');
  const empty = getRefElement(element, 'empty');
  const methods = getRefElement(element, 'methods');

  loading?.classList.add(CLASS_HIDDEN);
  error?.classList.add(CLASS_HIDDEN);
  empty?.classList.remove(CLASS_HIDDEN);
  methods?.classList.add(CLASS_HIDDEN);
}

/**
 * Render shipping methods
 * @param {HTMLElement} element - Component element
 * @param {ShippingData} data - Shipping data
 * @param {string} [fallbackCurrency] - Fallback currency code
 */
export function renderShippingMethods(element, data, fallbackCurrency = '') {
  const loading = getRefElement(element, 'loading');
  const error = getRefElement(element, 'error');
  const empty = getRefElement(element, 'empty');
  const methods = getRefElement(element, 'methods');

  loading?.classList.add(CLASS_HIDDEN);
  error?.classList.add(CLASS_HIDDEN);
  empty?.classList.add(CLASS_HIDDEN);

  if (!data.shippingRates || data.shippingRates.length === 0) {
    showEmptyState(element);
    return;
  }

  const rates = data.shippingRates.slice(0, MAX_RATES_DISPLAY);
  if (!methods) return;

  const card = element.closest(SELECTOR_SHIPPING_CARD);
  if (card instanceof HTMLElement) {
    if (rates.length === 1) {
      card.classList.add('single-option');
    } else {
      card.classList.remove('single-option');
    }
  }

  const currency = data.currencyCode || element.dataset.currency || fallbackCurrency;
  const currencySymbol = getCurrencySymbol(element);

  methods.innerHTML = rates.map((/** @type {ShippingRate} */ rate) => {
    const cost = formatPrice(rate.cost, currency, currencySymbol);
    const methodName = rate.courierName || SHIPPING_DEFAULT;

    return `
      <div class="globale-shipping-method">
        <div class="globale-shipping-method-content">
          <span class="globale-shipping-method-bullet"></span>
          <span class="globale-shipping-method-name">${escapeHtml(methodName)}</span>
        </div>
        <span class="globale-shipping-method-cost">${cost}</span>
      </div>
    `;
  }).join('');

  methods.classList.remove(CLASS_HIDDEN);
}

/**
 * Handle shipping fetch errors
 * @param {Error} error - The error object
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.showError - Function to show error message
 * @param {Function} callbacks.showEmptyState - Function to show empty state
 * @param {Object} [errorMessages] - Custom error message keys
 * @param {string} [errorMessages.amountNotAvailable] - Translation key for amount not available error
 * @param {string} [errorMessages.productCodesNotAvailable] - Translation key for product codes not available error
 */
export function handleShippingError(error, callbacks, errorMessages = {}) {
  const { showError, showEmptyState } = callbacks;

  if (error?.message === 'Data not available') {
    showEmptyState();
  } else if (error?.message === 'Unable to detect country') {
    showError(getTranslation('globale.error_unable_detect_country'));
  } else if (error?.message === 'Amount is not available' || 
             error?.message === 'Cart total is not available' || 
             error?.message === 'Product price is not available. Please select a variant.') {
    const errorKey = errorMessages.amountNotAvailable || 
                     (error?.message?.includes('Cart total') ? 'globale.error_cart_total_not_available' : 'globale.error_product_price_not_available');
    showError(getTranslation(errorKey));
  } else if (error?.message === 'Product codes are not available' || 
             error?.message === 'No products in cart' || 
             error?.message === 'Product code is not available.') {
    const errorKey = errorMessages.productCodesNotAvailable || 
                     (error?.message?.includes('cart') ? 'globale.error_no_products_in_cart' : 'globale.error_product_code_not_available');
    showError(getTranslation(errorKey));
  } else if (error?.message?.includes(ERROR_FAILED_FETCH) || 
             (error?.name === 'TypeError' && error?.message?.includes('fetch'))) {
    showError(getTranslation('globale.error_api_not_accessible'));
  } else if (error?.message?.includes(ERROR_PROXY) || error?.message?.includes('proxy')) {
    showError(getTranslation('globale.error_proxy', { error: error?.message || 'Unknown error' }));
  } else {
    showError(getTranslation('globale.error_shipping_unknown', { error: error?.message || 'Unknown error' }));
  }
}
